import React from 'react';
import { history, useIntl } from 'umi';
import { TableColumn } from 'tea-component';
import { getCountryFlag } from '@home/_config/countries';
import EllipsisHover from '@home/components/EllipsisHover';
import SortBy from '@intelligence/components/TableComponents/components/SortBy';
import FirstRow from '@intelligence/components/TableComponents/components/FirstRow';
import { getDetailGamePathCommon } from '@intelligence/utils/topchartsUtils';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { getPlatformIconsImg, iconSortFun } from '@intelligence/utils/common';
import { CommonColumnBuilders } from '@intelligence/v3/utils/columns';
import CommonColumns from '@intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import { dbiTopChartsBtnLogMes } from '@intelligence/utils/topChartsBuriedPointCfg';
import {
  getAlineaDateValue,
  getAlineaMetricText,
  getAlineaPlatformIconKeys,
  getAlineaPlatformText,
  getAlineaRegionText,
  getAlineaSourceQualityText,
  getAlineaValue,
  formatAlineaValue,
} from './utils';
import { LANGUAGE_CN } from '@home/_config/config';

import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';
import type { IAlineaCalibrationRecord, IAlineaCalibrationSortState } from './types';

interface IUseAlineaCalibrationColumnsParams {
  sortState: IAlineaCalibrationSortState;
  onSortChange: (sortState: IAlineaCalibrationSortState) => void;
  regionOptions: IRecord[];
  genreOptions: IRecord;
}

const SORT_TITLE_MAP: IRecord<string> = {
  date: 'dbi_date',
  value: 'dbi_value',
};

const getGameId = (record: IAlineaCalibrationRecord): string => {
  return record?.unified_id || '';
};

const getGameName = (record: IAlineaCalibrationRecord): string => {
  return record?.name || '-';
};

const getAlineaTrueDataPath = (record: IAlineaCalibrationRecord): string => {
  const path = getDetailGamePathCommon({ entityType: 'combine', gameId: getGameId(record), tab: 'stats', subTab: 'overview' });
  return path ? `${path}${path.includes('?') ? '&' : '?'}openTrueData=alinea` : '';
};

const handleAlineaTrueDataDrilldown = (record: IAlineaCalibrationRecord): void => {
  const path = getAlineaTrueDataPath(record);
  if (!path) return;
  const logConfig = dbiTopChartsBtnLogMes.combine_PCConsoleAlineaAnalyticsCalibration_TrueDataDrilldown;
  const gameId = getGameId(record);
  buriedBtnPoint({
    ...logConfig,
    extInfo: gameId ? `${logConfig.extInfo}: (${gameId})` : logConfig.extInfo,
  });
  history.push(path);
};

const renderSortableHeader = (
  key: string,
  formatMessage: (descriptor: { id: string }) => string,
  sortState: IAlineaCalibrationSortState,
  onSortChange: (sortState: IAlineaCalibrationSortState) => void,
  align: 'left' | 'right' = 'left',
): React.ReactNode => {
  const titleKey = SORT_TITLE_MAP[key] || key;
  const title = titleKey.startsWith('dbi_') ? formatMessage({ id: titleKey }) : titleKey;
  let sortValue: '' | 'asc' | 'desc' = '';
  if (sortState.order_metric === key) {
    sortValue = sortState.order;
  }

  return (
    <SortBy
      align={align}
      title={title}
      defaultKey={sortState.order_metric}
      defaultValue={sortValue}
      list={[
        {
          key,
          sortMetric: key,
          title,
          text: title,
        },
      ]}
      onChange={(payload: SortByItemType) => {
        let nextOrder: 'asc' | 'desc' = 'desc';
        if (payload?.sortVal === 'asc') {
          nextOrder = 'asc';
        }
        onSortChange({
          order_metric: key,
          order: nextOrder,
        });
      }}
    />
  );
};

const getPublisherName = (record: IAlineaCalibrationRecord): string => {
  return record?.publisher || '';
};

const getPublisherCountry = (record: IAlineaCalibrationRecord): string => {
  return record?.publisher_addr || '';
};

const getPublisherFlag = (record: IAlineaCalibrationRecord): string => {
  const publisherCountry = getPublisherCountry(record);
  if (!publisherCountry) {
    return '';
  }

  return getCountryFlag(publisherCountry);
};

const openSourceLink = (source: string): void => {
  window.open(source, '_blank');
};

export const useAlineaCalibrationColumns = (
  params: IUseAlineaCalibrationColumnsParams,
): TableColumn<IAlineaCalibrationRecord>[] => {
  const { sortState, onSortChange, regionOptions, genreOptions } = params;
  const { locale, formatMessage } = useIntl();
  const isCN = locale === LANGUAGE_CN;

  return [
    {
      key: 'game',
      header: formatMessage({ id: 'dbi_game' }),
      width: 300,
      fixed: 'left',
      render: (record) => {
        const gamePath = getAlineaTrueDataPath(record);
        const platformIcon = iconSortFun(getAlineaPlatformIconKeys(record?.platform), [
          'steam',
          'epic',
          'pc',
          'ps',
          'xbox',
          'nintendo',
        ]);
        const icons = Array.from(new Set(getPlatformIconsImg(platformIcon)));
        const entityIcons = iconSortFun(
          Array.from(new Set(record?.entity_icon || [])),
          ['mobile', 'pc', 'console'],
        );

        return (
          <FirstRow
            className="dbi-alinea-calibration__game-link"
            cvm={{
              img: record?.cover || '',
              gametitle: getGameName(record),
              countryIcon: getPublisherFlag(record),
              text: getPublisherName(record),
              imgType: 'combine',
              icon: icons.length ? icons : undefined,
              entityIcon: record?.entity_icon?.length ? (
                <>
                  {entityIcons.map((plat) => (
                    <a className="dbi-pcconsole-table-firstrow" key={plat}>
                      <SvgIcon
                        className="dbi-pcconsole-table-entityicon"
                        type={plat === 'pc' ? 'monitor' : 'game'}
                      />
                    </a>
                  ))}
                </>
              ) : null,
            }}
            rihgtClickToJumpHref={gamePath}
            imgType="combine"
            onClick={() => {
              if (!gamePath) {
                return;
              }

              handleAlineaTrueDataDrilldown(record);
            }}
          />
        );
      },
    },
    {
      key: 'metric',
      header: formatMessage({ id: 'dbi_label_metrics' }),
      width: isCN ? 140 : 190,
      render: (record) => {
        return getAlineaMetricText(record, locale);
      },
    },
    {
      key: 'region',
      header: formatMessage({ id: 'dbi_region' }),
      width: 180,
      align: 'left',
      render: (record) => {
        return getAlineaRegionText(record, regionOptions, locale);
      },
    },
    {
      key: 'release_time',
      header: formatMessage({ id: 'dbi_release' }),
      width: 140,
      render: (record) => {
        return formatReleaseTime(locale, {
          isSpecialReleaseTime: record?.is_special_release_time,
          releaseTime: record?.release_time,
          releaseTimeEN: record?.release_time_en,
          releaseTimeCN: record?.release_time_cn,
        });
      },
    },
    {
      key: 'platform',
      header: formatMessage({ id: 'dbi_platform' }),
      width: 200,
      render: (record) => {
        const platformText = getAlineaPlatformText(record);
        return <EllipsisHover className="dbi-alinea-calibration__platform-text" text={platformText} />;
      },
    },
    {
      key: 'date',
      header: renderSortableHeader('date', formatMessage, sortState, onSortChange, 'left'),
      width: 130,
      align: 'left',
      render: (record) => {
        return getAlineaDateValue(record) || '-';
      },
    },
    {
      key: 'value',
      header: renderSortableHeader('value', formatMessage, sortState, onSortChange, 'right'),
      width: 140,
      align: 'right',
      render: (record) => {
        const value = getAlineaValue(record);
        if (value === undefined || value === null || value === '') {
          return '-';
        }

        return formatAlineaValue(value);
      },
    },
    CommonColumns.GenreColumn<IAlineaCalibrationRecord>({
      locale,
      width: 220,
      genreSource: 'iegg',
      mainGenreField: 'iegg_genre',
      subGenreField: 'iegg_sub_genre',
      genreOptionsMap: genreOptions,
    }),
    CommonColumnBuilders.buildPublisherClassColumn<IAlineaCalibrationRecord>({
      locale,
      formatMessage,
    }),
    {
      key: 'source_quality',
      header: formatMessage({ id: 'dbi_source_quality' }),
      width: isCN ? 120 : 150,
      render: (record) => {
        return getAlineaSourceQualityText(record?.source_quality, locale);
      },
    },
    {
      key: 'source',
      header: formatMessage({ id: 'dbi_source' }),
      width: 100,
      render: (record) => {
        const source = record?.source;
        if (source) {
          return (
            <SvgIcon
              // type="aigc-share"
              // type="aigc-jump"
              type="other-share-icon"
              className="dbi-table-icon-link"
              onClick={(event) => {
                event?.stopPropagation();
                event?.preventDefault();
                openSourceLink(source);
              }}
            />
          );
        }

        return '-';
      },
    },
  ];
};
